import {Injectable} from "@angular/core";
import {DateTime} from "@lib/date-time";
import {BehaviorSubject,combineLatest,concatMap,from,mergeMap,of,op, switchMap} from "@lib/rxjs";
import {storageLocal} from "@lib/storage-local";
import {User,fragQuery} from "@models";
import {WebClient} from "./web-client";
import { fixDates, fixTags, FragQueryService } from "./frag-query.service";
import { toZonedTime } from "date-fns-tz";
import { getUserIANATimezone } from "@lib/timezone-preference-list";

interface DurationOption {
	label: string;
	type: DateTime.Step | 'custom';
	key?: string;
}

const durationOptions: DurationOption[] = [
	{
		label: 'last hour',
		type: [1,'hour'],
	},
	{
		label: 'Last 3 Hours',
		type: [3,'hour'],
	},
	{
		label: 'Last 6 Hours',
		type: [6,'hour'],
	},
	{
		label: 'Last 12 Hours',
		type: [12,'hour'],
	},
	{
		label: 'Last 24 Hours',
		type: [1,'day'],
	},
	{
		label: 'Custom',
		type: 'custom',
	}
];
for (const opt of durationOptions)
	opt.key = Array.isArray(opt.type) ? opt.type.join('') : opt.type;
const durationOptionMap = new Map(durationOptions.map((v) => [v.key, v]));

function liveTimeRange(step:DateTime.Step){
	const end=new DateTime();
	const begin=end.clone().sub(step);
	return {
		begin,
		end,
	};
}

@Injectable({
	providedIn: "root",
})
export class HomeFragQueryService{
	constructor(
		private readonly webClient:WebClient,
		private readonly user: User,
		private  readonly fragQuery: FragQueryService
	) {
		this.startTimer();
		this.fragQuery.trigger$.pipe(
			op.debounceTime(100),
			op.map(()=>{
				this.refresh();
			})
		).subscribe()
	}

	private readonly refresInMinutes: number = 15;
    public readonly homeduration$ = new BehaviorSubject(durationOptionMap.get('1hour').key);
	public readonly homeDevice$ = new BehaviorSubject(null);
	public readonly allDeviceData$ = new BehaviorSubject<fragQuery.timeline.sieveSizesAtPercents.Response[]>([]);
    public readonly begin$ = new BehaviorSubject(new DateTime(storageLocal.get('frag-query-begin') ?? (DateTime.now() - (60 * 60 * 1000))));
	public readonly end$ = new BehaviorSubject(new DateTime(storageLocal.get('frag-query-end') ?? DateTime.now()));
	public readonly minutes=new BehaviorSubject(0);
    public readonly sec=new BehaviorSubject(0);
	public readonly devices$ =this.webClient.model.device.search$({
		sort: {
			clientId: '+',
			id: '+'
		}
	})

	public readonly homeTimeRange$ = this.homeduration$.pipe(
		op.map(key => durationOptionMap.get(key)),
		op.switchMap(durOpt => {
			if (durOpt.type === 'custom') {
				return combineLatest([this.begin$, this.end$])
					.pipe(op.map(([begin, end]) => ({ begin, end })));
			}
			const step = durOpt.type;
			return of(liveTimeRange(step));
		})
	)

	startTimer(){
        var secInt=setInterval(()=>{
            if(this.sec.getValue()===59){
                if(this.minutes.value===(this.refresInMinutes-1)){
                    this.refresh();
                }
				this.sec.next(0);
            }else{
                this.sec.next(this.sec.value+1);
            }
        },1000);
        var minInt=setInterval(()=>{
            if(this.minutes.value==this.refresInMinutes-1){
                this.minutes.next(0);
            }else{
                this.minutes.next(this.minutes.value+1);
            }
        },60*1000)
    }

	

	refresh() {
        this.allDeviceData$.next([]);
		this.getAllDeviceData();
    }

    getAllDeviceData() {
        this.homeTimeRange$.subscribe(
			range => {
				this.devices$.pipe(
					mergeMap(devices =>
						from(devices)
						.pipe(
							concatMap(async (device) => {
								const body: fragQuery.timeline.sieveSizesAtPercents.Body = {
									email: this.user.email,
									authToken: this.user.authToken,
									deviceId: device.id,
									begin: range.begin,
									end: range.end,
									weightType: "area",
									percents: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
									chunkSize: 0, // default chunk size
								};
								const result = await this.webClient
									.rest({
										route: 'frag-query/timeline/sieve-sizes-at-percents',
										method: 'get',
										body,
									})
								const transformedResponse = result.toRecord<fragQuery.timeline.sieveSizesAtPercents.Response>();
								return { transformedResponse, device };
							})
						)
					)
				)
				.subscribe(({transformedResponse, device}) => {
					const formattedResponse = {
						deviceId: transformedResponse.deviceId,
						siteId: device.siteId,
						begin:
							this.user.timezone === null || this.user.timezone === 'Default'
								? new DateTime(transformedResponse.begin)
								: new DateTime(toZonedTime(transformedResponse.begin, getUserIANATimezone(this.user.timezone))),
						end:
							this.user.timezone === null || this.user.timezone === 'Default'
								? new DateTime(transformedResponse.end)
								: new DateTime(toZonedTime(transformedResponse.end, getUserIANATimezone(this.user.timezone))),
						percents: transformedResponse.percents,
						intervals: fixDates(
							transformedResponse.intervals,
							this.user.timezone
						),
						tags: fixTags(transformedResponse.tags),
						summary: transformedResponse.summary,
						totalIntervalCount: transformedResponse.totalIntervalCount
					};
					this.allDeviceData$.value.push(formattedResponse);
				});
			});
	}
}